import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const CambodiaPage = () => {
  const cambodiaData = {
    name: 'Cambodia',
    region: 'Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Cambodia | ACTD Dossiers',
    metaDescription: 'Learn how to register pharma and nutraceutical products in Cambodia. ACTD dossier requirements, Khmer label rules, key documents, and regulatory pathways for a growing, import-dependent market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Cambodia',
      subtitle: 'A growing, import-dependent market with ACTD-aligned requirements, rising chronic disease burden, and increasing demand for quality generics and supplements.',
      cta: 'Start Your Cambodia Registration',
      secondaryCta: 'Download Cambodia Readiness Checklist'
    },
    
    atAGlance: [
      { label: 'Population', value: '17.4M' },
      { label: 'Urbanisation', value: '22% urban' },
      { label: 'Working-age population', value: '66%' },
      { label: 'Healthcare spending', value: '$109 per capita' },
      { label: 'Hospital beds per 1,000', value: '0.7' },
      { label: 'Physicians per 1,000', value: '0.21' }
    ],
    
    marketOverview: {
      title: 'Market Overview: High Dependence on Imports',
      stats: [
        'Cambodia imported about $339 million in pharmaceuticals in 2023',
        'India is the second-largest supplier (15%), after China (62%)',
        'Only 33 licensed domestic manufacturers with limited local competition',
        'Nutraceutical market estimated at $20-30 million, driven by rising health awareness'
      ],
      opportunities: [
        'High import reliance (85% of medicines imported)',
        'Growing chronic disease burden',
        'Faster acceptance of Indian dossiers due to regional familiarity',
        'Strong retail presence & pharmacy chains'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: What Drives Demand?',
      subtitle: 'Cambodia is transitioning from infectious disease dominance to chronic care demand.',
      categories: [
        {
          name: 'Chronic Diseases',
          items: [
            'Cardiovascular diseases - 13-15% of all deaths',
            'Diabetes - 6.3% prevalence, rising steadily',
            'Cancers - 14% of deaths',
            'Mental health - 10-12% prevalence'
          ]
        },
        {
          name: 'Infectious Diseases',
          items: [
            'TB - 335 cases per 100,000',
            'Dengue - 21,500+ cases in 2023',
            'Malaria - almost eliminated'
          ]
        },
        {
          name: 'Nutrition / Wellness',
          items: [
            'Multivitamins',
            'Omega-3',
            'Herbal boosters',
            'Probiotics',
            'Calcium + Vitamin D'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Department of Drugs and Food (DDF)',
      description: 'Under the Ministry of Health (MoH), Cambodia. All product registrations (pharma & nutraceuticals) are overseen by DDF.',
      pharma: {
        timeline: '3-6 months, if dossier is complete & stable',
        requirements: [
          'ACTD format dossier',
          'GMP certificate from exporting country (India accepted)',
          'Certificate of Pharmaceutical Product (CoPP)',
          'Stability data (Zone IVb - hot/humid)',
          'Product samples',
          'Analytical methods & specifications'
        ],
        insight: 'Cambodia frequently references decision of other countries - well-prepared ACTD dossiers often pass without major queries.'
      },
      nutra: {
        timeline: '2-4 months, depending on ingredient acceptance',
        requirements: [
          'Dossier with formulation & COA',
          'GMP certificate',
          'Safety data / justification',
          'Mandatory Khmer-language label',
          'Artwork approval before submission',
          'Allowed & restricted claims review'
        ],
        insight: 'Fast-track approval possible for herbal / vitamin-mineral products with clean documentation.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharma Registration Process (ACTD)',
      steps: [
        'Product classification',
        'Appointment of local importer',
        'ACTD dossier compilation (Modules 1-5)',
        'Artwork / label compliance',
        'Submission to MoH',
        'Query handling',
        'Final approval & registration certificate'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical Registration Steps',
      steps: [
        'Category confirmation (supplement / food)',
        'Formulation review (ingredient acceptability)',
        'COA, stability, safety data preparation',
        'Khmer artwork compliance',
        'Submission to MoH',
        'Clarifications (if any)',
        'Final approval'
      ]
    },
    
    requiredDocs: {
      'Pharma (ACTD)': [
        'Module 1-5 CTD/ACTD',
        'GMP certificate',
        'Certificate of Pharmaceutical Product',
        'Formula sheet',
        'Stability data (Zone IVb)',
        'Label/pack insert',
        'Sample COA'
      ],
      'Nutraceuticals': [
        'Product formula + COA',
        'Safety data',
        'Ingredient justification',
        'Manufacturer GMP',
        'Khmer artwork',
        'Claims justification'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Cambodia Should Be On Your Expansion List',
      reasons: [
        '85% import dependence',
        'Very few local manufacturers',
        'Fast approvals for ACTD dossiers',
        'Strong demand for chronic care',
        'Large pharmacy chains for OTC/nutra success',
        'NGOs & government tenders purchase chronic & paediatric meds'
      ],
      bestPortfolio: [
        '10-15 high-volume + high-value molecules',
        'Diabetes + hypertension + paediatrics + infectious care',
        'Nutra add-ons: Vitamin C+Zinc, Omega-3, CoQ10, Probiotics'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Supports Cambodia Registration',
      services: [
        'Market mapping + feasibility',
        'ACTD dossier compilation',
        'Khmer label & artwork compliance',
        'Translation support',
        'Query & deficiency handling',
        'Submission kit for importer',
        'Lifecycle management (renewals & variations)'
      ]
    },
    
    faqs: [
      {
        question: 'Is ACTD mandatory for Cambodia?',
        answer: 'Yes. Cambodia follows an ACTD-aligned dossier structure for generics.'
      },
      {
        question: 'Do nutraceuticals require Khmer labelling?',
        answer: 'Yes, Khmer is mandatory for ingredient panel & claims.'
      },
      {
        question: 'Does Cambodia accept Indian GMP?',
        answer: 'Yes, Indian GMP is accepted for both pharma & nutraceuticals.'
      },
      {
        question: 'Are fast-track options available?',
        answer: 'Yes, especially for herbal, vitamin-mineral supplements with clean documentation.'
      },
      {
        question: 'Can one importer handle multiple products?',
        answer: 'Yes. A single authorized importer can represent your entire portfolio.'
      }
    ]
  };

  return <CountryTemplate countryData={cambodiaData} />;
};
